/* Shared extras for the canvas designers: badge reels and other dome-label products
 * (sections/custom-product-designer.liquid), custom badges (credential-designer.liquid)
 * and UV-printed holders (uv-product-preview.liquid). Patrick, 2026-10-06: bring over
 * what worked on the custom badge buddy pages (custom-bb.js).
 *
 * 1. [data-spid-rail]: the thumbnail strip becomes one scrolling row with arrows,
 *    not a wrapping grid.
 * 2. [data-spid-mini-stage]: on phones, once the design preview scrolls out of view
 *    while the shopper is still in the buy box, a small live copy floats under the
 *    header; tap it to scroll back. These designers draw on <canvas>, which can't be
 *    cloned, so the copy is repainted from the stage's visible images, inline SVG
 *    drawings (the reel body) and canvases - photos and SVGs first, canvases on top,
 *    at their on-screen positions - about four times a second while it shows.
 */
(function () {
  function rails() {
    document.querySelectorAll('[data-spid-rail]').forEach(function (strip) {
      if (strip.parentNode.classList.contains('spid-rail')) return;
      var rail = document.createElement('div');
      rail.className = 'spid-rail';
      strip.parentNode.insertBefore(rail, strip);
      rail.appendChild(strip);
      function arrow(dir, label, glyph) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'spid-rail__arrow spid-rail__arrow--' + dir;
        b.setAttribute('aria-label', label);
        b.innerHTML = '<span aria-hidden="true">' + glyph + '</span>';
        b.hidden = true;
        b.addEventListener('click', function () {
          var d = dir === 'prev' ? -1 : 1;
          if (vertical()) strip.scrollBy({ top: d * strip.clientHeight * 0.85, behavior: 'smooth' });
          else strip.scrollBy({ left: d * strip.clientWidth * 0.85, behavior: 'smooth' });
        });
        rail.appendChild(b);
        return b;
      }
      var prev = arrow('prev', 'Previous photos', '‹');
      var next = arrow('next', 'More photos', '›');
      // A strip laid out as a column (thumbnails down the left of the photo on desktop, 2026-10-08)
      // scrolls up and down; the arrows follow.
      function vertical() { return getComputedStyle(strip).flexDirection === 'column'; }
      function sync() {
        var v = vertical();
        rail.classList.toggle('spid-rail--v', v);
        var pos = v ? strip.scrollTop : strip.scrollLeft;
        var max = v ? strip.scrollHeight - strip.clientHeight : strip.scrollWidth - strip.clientWidth;
        prev.hidden = pos <= 2;
        next.hidden = pos >= max - 2;
      }
      strip.addEventListener('scroll', sync, { passive: true });
      if (window.ResizeObserver) new ResizeObserver(sync).observe(strip);
      sync();
    });
  }

  function mini() {
    var stage = document.querySelector('[data-spid-mini-stage]');
    var info = document.querySelector('.product__info-container');
    if (!stage || !info || document.querySelector('.spid-mini')) return;
    var mq = window.matchMedia('(max-width: 749px)');
    var MAX_H = 96, MAX_W = 150;

    var box = document.createElement('button');
    box.type = 'button';
    box.className = 'spid-mini';
    box.tabIndex = -1;
    box.setAttribute('aria-label', 'Your design. Tap to see the full preview.');
    var cv = document.createElement('canvas');
    cv.className = 'spid-mini__canvas';
    var label = document.createElement('span');
    label.className = 'spid-mini__label';
    label.textContent = 'Your design';
    box.appendChild(cv);
    box.appendChild(label);
    document.body.appendChild(box);

    var shown = false, timer = null, queued = false;
    var svgCache = [];

    // An inline <svg> is drawn by serializing a sized copy into an image; the image is
    // rebuilt only when the markup changes (a new reel colour), and the previous one is
    // used until the new one has loaded.
    function svgImage(el, r) {
      var copy = el.cloneNode(true);
      copy.setAttribute('width', Math.round(r.width));
      copy.setAttribute('height', Math.round(r.height));
      if (!copy.getAttribute('xmlns')) copy.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      var markup = new XMLSerializer().serializeToString(copy);
      var hit = null;
      for (var i = 0; i < svgCache.length; i++) if (svgCache[i].el === el) hit = svgCache[i];
      if (!hit) { hit = { el: el, markup: '', img: null, next: null }; svgCache.push(hit); }
      if (hit.markup !== markup) {
        hit.markup = markup;
        var img = new Image();
        img.onload = function () { hit.img = img; };
        img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(markup);
      }
      return hit.img;
    }

    function headerBottom() {
      var h = document.querySelector('sticky-header, .section-header');
      var b = h ? h.getBoundingClientRect().bottom : 0;
      return b > 0 ? b : 0;
    }

    function visible(el) {
      if (el.closest('[hidden]')) return false;
      var r = el.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) return false;
      for (var n = el; n && n !== stage.parentNode; n = n.parentElement) {
        var cs = getComputedStyle(n);
        if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.05) return false;
      }
      return true;
    }

    function paint() {
      var layers = Array.prototype.filter.call(stage.querySelectorAll('img, canvas, svg'), function (el) {
        if (el.tagName.toLowerCase() === 'svg' && (el.parentElement.closest('svg') || el.closest('button'))) return false;
        return visible(el);
      });
      if (!layers.length) return;
      // Photos and drawings under canvases: the designers draw the artwork on a canvas on top.
      layers.sort(function (a, b) { return (a.tagName === 'CANVAS') - (b.tagName === 'CANVAS'); });
      var x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
      var rects = layers.map(function (el) {
        var r = el.getBoundingClientRect();
        x0 = Math.min(x0, r.left); y0 = Math.min(y0, r.top);
        x1 = Math.max(x1, r.right); y1 = Math.max(y1, r.bottom);
        return r;
      });
      // Never wider than the stage itself: it crops what overflows it on screen too.
      var sr = stage.getBoundingClientRect();
      x0 = Math.max(x0, sr.left); y0 = Math.max(y0, sr.top);
      x1 = Math.min(x1, sr.right); y1 = Math.min(y1, sr.bottom);
      var w = x1 - x0, h = y1 - y0;
      if (w < 2 || h < 2) return;
      var k = Math.min(MAX_H / h, MAX_W / w);
      var dpr = window.devicePixelRatio || 1;
      var cw = Math.round(w * k), ch = Math.round(h * k);
      if (cv.width !== cw * dpr || cv.height !== ch * dpr) {
        cv.width = cw * dpr; cv.height = ch * dpr;
        cv.style.width = cw + 'px'; cv.style.height = ch + 'px';
      }
      var c = cv.getContext('2d');
      c.setTransform(dpr * k, 0, 0, dpr * k, 0, 0);
      c.clearRect(0, 0, w, h);
      layers.forEach(function (el, i) {
        var r = rects[i];
        try {
          if (el.tagName.toLowerCase() === 'svg') {
            var si = svgImage(el, r);
            if (si) c.drawImage(si, r.left - x0, r.top - y0, r.width, r.height);
          } else if (el.tagName === 'IMG') {
            if (!el.complete || !el.naturalWidth) return;
            // object-fit: contain, the way these stages show their photos.
            var s = Math.min(r.width / el.naturalWidth, r.height / el.naturalHeight);
            var dw = el.naturalWidth * s, dh = el.naturalHeight * s;
            c.drawImage(el, r.left - x0 + (r.width - dw) / 2, r.top - y0 + (r.height - dh) / 2, dw, dh);
          } else {
            c.drawImage(el, r.left - x0, r.top - y0, r.width, r.height);
          }
        } catch (e) { /* a layer that can't be drawn is skipped, never fatal */ }
      });
    }

    function check() {
      queued = false;
      var hb = headerBottom();
      var on = mq.matches &&
        stage.getBoundingClientRect().bottom < hb + 40 &&
        info.getBoundingClientRect().bottom > hb + MAX_H + 60;
      if (on !== shown) {
        shown = on;
        box.classList.toggle('is-on', on);
        box.tabIndex = on ? 0 : -1;
        if (on) { paint(); timer = setInterval(paint, 250); }
        else if (timer) { clearInterval(timer); timer = null; }
      }
      if (on) box.style.top = Math.round(hb + 8) + 'px';
    }
    function queue() { if (!queued) { queued = true; requestAnimationFrame(check); } }

    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    box.addEventListener('click', function () {
      var top = stage.getBoundingClientRect().top + window.pageYOffset - headerBottom() - 12;
      window.scrollTo({ top: top, behavior: 'smooth' });
    });
    queue();
  }

  function start() { rails(); mini(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
